/**
 * PipelineErrorsAdmin - Super Admin page showing pipeline failures.
 *
 * Queries multiple tables for error states:
 * - session_recordings WHERE status = 'error'
 * - processing_jobs WHERE status = 'failed'
 * - sessions WHERE status = 'failed'
 *
 * Auto-refreshes every 30 seconds.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { RefreshCw, AlertTriangle, CheckCircle, Trash2 } from 'lucide-react';

interface PipelineError {
  id: string;
  source: 'recording' | 'processing_job' | 'session';
  sessionId: string;
  errorMessage: string;
  detail: string;
  timestamp: string;
}

export default function PipelineErrorsAdmin() {
  const [errors, setErrors] = useState<PipelineError[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchErrors = useCallback(async () => {
    setIsLoading(true);
    const results: PipelineError[] = [];

    // 1. Failed session_recordings
    const { data: recordings } = await supabase
      .from('session_recordings')
      .select('id, session_id, phase, status, error_message, created_at')
      .eq('status', 'error')
      .order('created_at', { ascending: false })
      .limit(50);

    for (const r of recordings || []) {
      results.push({
        id: `rec-${r.id}`,
        source: 'recording',
        sessionId: r.session_id,
        errorMessage: r.error_message || 'Unknown recording error',
        detail: `Phase: ${r.phase}`,
        timestamp: r.created_at,
      });
    }

    // 2. Failed processing_jobs
    const { data: jobs } = await supabase
      .from('processing_jobs')
      .select('id, session_id, job_type, status, error_message, created_at')
      .eq('status', 'failed')
      .order('created_at', { ascending: false })
      .limit(50);

    for (const j of jobs || []) {
      results.push({
        id: `job-${j.id}`,
        source: 'processing_job',
        sessionId: j.session_id,
        errorMessage: j.error_message || 'Unknown job error',
        detail: `Job type: ${j.job_type}`,
        timestamp: j.created_at,
      });
    }

    // 3. Failed sessions
    const { data: sessions } = await supabase
      .from('sessions')
      .select('id, status, processing_error, updated_at')
      .eq('status', 'failed')
      .order('updated_at', { ascending: false })
      .limit(50);

    for (const s of sessions || []) {
      results.push({
        id: `sess-${s.id}`,
        source: 'session',
        sessionId: s.id,
        errorMessage: (s as Record<string, unknown>).processing_error as string || 'Session marked as failed',
        detail: 'Session status: failed',
        timestamp: s.updated_at,
      });
    }

    // Sort by timestamp descending
    results.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    setErrors(results);
    setIsLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => {
    fetchErrors();
    const interval = setInterval(fetchErrors, 30000); // Auto-refresh every 30s
    return () => clearInterval(interval);
  }, [fetchErrors]);

  const handleDismiss = async (error: PipelineError) => {
    if (error.source === 'recording') {
      const realId = error.id.replace('rec-', '');
      await supabase
        .from('session_recordings')
        .update({ status: 'dismissed', error_message: null })
        .eq('id', realId);
    } else if (error.source === 'processing_job') {
      const realId = error.id.replace('job-', '');
      await supabase
        .from('processing_jobs')
        .update({ status: 'dismissed' })
        .eq('id', realId);
    } else if (error.source === 'session') {
      const realId = error.id.replace('sess-', '');
      await supabase
        .from('sessions')
        .update({ status: 'draft' })
        .eq('id', realId);
    }
    fetchErrors();
  };

  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const sourceLabel = (s: PipelineError['source']) => {
    switch (s) {
      case 'recording': return 'Recording';
      case 'processing_job': return 'Processing Job';
      case 'session': return 'Session';
    }
  };

  const sourceBadgeColor = (s: PipelineError['source']) => {
    switch (s) {
      case 'recording': return 'bg-red-100 text-red-700';
      case 'processing_job': return 'bg-orange-100 text-orange-700';
      case 'session': return 'bg-yellow-100 text-yellow-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">Pipeline Errors</h1>
          <p className="text-sm text-steel mt-1">
            Auto-refreshes every 30s. Last refresh: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <button
          onClick={fetchErrors}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-steel/30 rounded-lg
                     hover:bg-gray-50 text-navy font-medium text-sm disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary */}
      <div className="flex gap-4">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
          errors.length > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
        }`}>
          {errors.length > 0
            ? <AlertTriangle className="h-5 w-5" />
            : <CheckCircle className="h-5 w-5" />
          }
          <span className="font-semibold">
            {errors.length > 0 ? `${errors.length} active error${errors.length !== 1 ? 's' : ''}` : 'No errors'}
          </span>
        </div>
      </div>

      {/* Error List */}
      {errors.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
          <p className="text-lg text-steel font-medium">All pipelines healthy</p>
          <p className="text-sm text-steel/60 mt-1">No failed recordings, jobs, or sessions</p>
        </div>
      ) : (
        <div className="space-y-3">
          {errors.map((err) => (
            <div key={err.id} className="bg-white border border-steel/20 rounded-lg p-4 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sourceBadgeColor(err.source)}`}>
                      {sourceLabel(err.source)}
                    </span>
                    <span className="text-xs text-steel">{formatTimestamp(err.timestamp)}</span>
                  </div>
                  <p className="text-sm font-mono text-navy/80 break-all">{err.errorMessage}</p>
                  <div className="flex gap-4 mt-2 text-xs text-steel">
                    <span>Session: <code className="text-navy">{err.sessionId.slice(0, 8)}...</code></span>
                    <span>{err.detail}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleDismiss(err)}
                  title="Dismiss error"
                  className="shrink-0 p-1.5 text-steel hover:text-red-500 hover:bg-red-50
                             rounded transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
